import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Loader2, Check, Undo2, Pin, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIEWS = [['open', 'Nog ophalen'], ['picked', 'Opgehaald'], ['all', 'Alles']];
const fmt = (d) => (d ? new Date(d).toLocaleDateString('nl-NL') : '—');

const Btn = ({ onClick, busy, className, testid, children }) => (
  <button onClick={onClick} disabled={busy} data-testid={testid}
          className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg disabled:opacity-40 transition-colors ${className}`}>
    {children}
  </button>
);

const Actions = ({ r, view, busy, act }) => {
  if (view === 'picked' || (view === 'all' && r.picked_up)) {
    return <Btn onClick={() => act([r.order_id], 'unpick')} busy={busy} testid={`unpick-${r.order_id}`}
                className="border border-zinc-300 text-zinc-700 hover:bg-zinc-100"><Undo2 className="w-3.5 h-3.5" />Terugzetten</Btn>;
  }
  if (view === 'all' && !r.in_route) {
    return <Btn onClick={() => act([r.order_id], 'pin')} busy={busy} testid={`pin-${r.order_id}`}
                className="border border-amber-300 text-amber-800 hover:bg-amber-50"><Pin className="w-3.5 h-3.5" />Moet opgehaald</Btn>;
  }
  return (
    <div className="flex gap-1.5">
      {r.old && (
        <Btn onClick={() => act([r.order_id], 'dismiss')} busy={busy} testid={`dismiss-${r.order_id}`}
             className="border border-zinc-300 text-zinc-600 hover:bg-zinc-100"><X className="w-3.5 h-3.5" />Niet meer ophalen</Btn>
      )}
      <Btn onClick={() => act([r.order_id], 'picked')} busy={busy} testid={`picked-${r.order_id}`}
           className="bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="w-3.5 h-3.5" />Opgehaald</Btn>
    </div>
  );
};

const Row = ({ r, view, busy, act }) => (
  <div className="flex flex-wrap md:grid md:grid-cols-[1.4fr_1fr_1fr_auto] gap-x-4 gap-y-1 items-center px-4 py-3 border-b border-zinc-100"
       data-testid={`all-order-row-${r.order_id}`}>
    <div className="min-w-0 basis-full md:basis-auto">
      <div className="font-semibold text-zinc-900 text-sm truncate">
        {`${r.make} ${r.model}`.trim() || 'Motor zonder naam'}
        {r.license_plate && <span className="ml-2 text-xs font-normal text-zinc-500">({r.license_plate})</span>}
      </div>
      <div className="text-[11px] text-zinc-500 truncate">
        #{r.order_number}
        {r.assigned_driver && !r.picked_up && <span className="ml-2 text-purple-700 font-semibold">· Op route: {r.assigned_driver}</span>}
      </div>
    </div>
    <div className="min-w-0 text-xs flex-1">
      <div className="text-zinc-400">Leverancier</div>
      <div className="text-zinc-800 truncate">{r.supplier}</div>
    </div>
    <div className="min-w-0 text-xs flex-1">
      <div className="text-zinc-400">{r.picked_up ? `Opgehaald ${fmt(r.picked_up_at)}` : `Besteld ${fmt(r.date)}${r.age_days != null ? ` · ${r.age_days} dagen` : ''}`}</div>
      <div className="text-zinc-800 truncate">{r.buyer || '—'}</div>
    </div>
    <Actions r={r} view={view} busy={busy} act={act} />
  </div>
);

export const AllOrdersTab = ({ onChanged }) => {
  const [q, setQ] = useState('');
  const [view, setView] = useState('open');
  const [data, setData] = useState({ total: 0, orders: [], old_days: 21 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const timer = useRef(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/chauffeur-all-orders`, { params: { q, view }, headers });
        setData(res.data);
      } catch (e) {
        toast.error('Bestellingen laden mislukt');
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, view, reload]);

  const act = async (ids, action) => {
    setBusy(true);
    try {
      if (action === 'pin') await axios.post(`${API}/admin/orders/needs-pickup`, { order_ids: ids, needs_pickup: true }, { headers });
      else await axios.post(`${API}/admin/orders/pickup-status`, { order_ids: ids, action }, { headers });
      const idSet = new Set(ids);
      setData((d) => ({ ...d, total: d.total - (view === 'all' ? 0 : ids.length),
        orders: view === 'all' ? d.orders : d.orders.filter((o) => !idSet.has(o.order_id)) }));
      toast.success({ picked: 'Afgevinkt als opgehaald', unpick: 'Teruggezet naar nog ophalen', dismiss: 'Haalt niemand meer op', pin: 'Staat weer op de ophaallijst' }[action]);
      if (view === 'all') setReload((n) => n + 1);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setBusy(false);
    }
  };

  const fresh = data.orders.filter((o) => !o.old);
  const old = data.orders.filter((o) => o.old);
  return (
    <div className="bg-white rounded-xl border border-zinc-200" data-testid="all-orders-tab">
      <div className="p-4 border-b border-zinc-200 space-y-3">
        <div className="flex flex-wrap gap-1.5" data-testid="pickup-view-tabs">
          {VIEWS.map(([k, label]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`pickup-view-${k}`}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${view === k ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'}`}>
              {label}{view === k && !loading ? ` (${data.total})` : ''}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op merk, model, kenteken, VIN, dealer of leverancier…"
                 className="w-full border border-zinc-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-purple-500"
                 data-testid="all-orders-search" />
        </div>
        {view === 'open' && <p className="text-xs text-zinc-500">Dit is precies wat in de route-planning staat. Opgehaald? Vink het af, dan verdwijnt het uit de route.</p>}
      </div>
      {loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-purple-600 animate-spin" /></div>}
      {!loading && data.orders.length === 0 && <div className="p-10 text-center text-sm text-zinc-500" data-testid="all-orders-empty">Niets gevonden.</div>}
      {!loading && fresh.map((r) => <Row key={r.order_id} r={r} view={view} busy={busy} act={act} />)}
      {!loading && old.length > 0 && (
        <div className="bg-zinc-50 border-t-4 border-zinc-200" data-testid="old-orders-block">
          <div className="px-4 py-3 flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="font-semibold text-zinc-800 text-sm">Oud: nog nodig? ({old.length})</div>
              <div className="text-xs text-zinc-500">Ouder dan {data.old_days} dagen en nog niet opgehaald</div>
            </div>
            <Btn onClick={() => window.confirm(`Alle ${old.length} oude motoren van de ophaallijst halen?`) && act(old.map((o) => o.order_id), 'dismiss')}
                 busy={busy} testid="dismiss-all-old" className="border border-zinc-300 text-zinc-700 hover:bg-zinc-100 bg-white">
              <X className="w-3.5 h-3.5" />Alle oude niet meer ophalen
            </Btn>
          </div>
          <div className="opacity-80">{old.map((r) => <Row key={r.order_id} r={r} view={view} busy={busy} act={act} />)}</div>
        </div>
      )}
    </div>
  );
};
